/**
 * Utility functions for processing AI-generated content
 */

/**
 * Clean AI-generated text by removing excessive newlines and normalizing spacing
 * @param text - Raw AI-generated text
 * @returns Cleaned text with proper formatting
 */
export function cleanAIText(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  return text
    // Remove unwanted characters (• bullet points and other special chars)
    .replace(/•/g, '')
    // Fix spacing before <strong> tags - add space before <strong> if there's no space
    .replace(/(\S)(?=<strong>)/g, '$1 ')
    // Remove ALL newline characters completely
    .replace(/\n/g, '')
    // Clean up multiple spaces
    .replace(/\s{2,}/g, ' ')
    // Remove leading/trailing whitespace
    .trim();
}

/**
 * Round percentage values to whole numbers
 * @param percentage - Percentage value (can be decimal)
 * @returns Rounded whole number percentage
 */
export function roundPercentage(percentage: number): number {
  if (typeof percentage !== 'number' || isNaN(percentage)) {
    return 0;
  }
  return Math.round(Math.max(0, Math.min(100, percentage)));
}

/**
 * Process AI-generated content with both text cleaning and percentage rounding
 * @param content - Raw content from AI
 * @param options - Processing options
 * @returns Processed content
 */
export interface ProcessAIContentOptions {
  cleanText?: boolean;
  roundPercentages?: boolean;
  preserveHtmlTags?: boolean;
}

export function processAIContent(
  content: string,
  options: ProcessAIContentOptions = {
    cleanText: true,
    roundPercentages: true,
    preserveHtmlTags: false
  }
): string {
  if (!content || typeof content !== 'string') {
    return '';
  }

  let processed = content;

  // Clean text if requested
  if (options.cleanText) {
    if (options.preserveHtmlTags) {
      // Preserve HTML tags while cleaning text around them
      processed = processed
        .replace(/>([^<]+)</g, (_, text) => {
          const cleaned = text
            .replace(/•/g, '')
            // Fix spacing before <strong> tags
            .replace(/(\S)(?=<strong>)/g, '$1 ')
            // Remove ALL newline characters completely
            .replace(/\n/g, '')
            .replace(/\s{2,}/g, ' ')
            .trim();
          return `>${cleaned}<`;
        });
    } else {
      processed = cleanAIText(processed);
    }
  }

  // Round percentages if requested
  if (options.roundPercentages) {
    processed = processed.replace(/(\d+(?:\.\d+)?)%/g, (_, match) => {
      const percentage = parseFloat(match);
      return `${roundPercentage(percentage)}%`;
    });
  }

  return processed;
}

/**
 * Extract and round all percentage values from text
 * @param text - Text containing percentages
 * @returns Array of rounded percentage values
 */
export function extractPercentages(text: string): number[] {
  if (!text || typeof text !== 'string') {
    return [];
  }

  const matches = text.match(/(\d+(?:\.\d+)?)%/g);
  if (!matches) {
    return [];
  }

  return matches.map(match => {
    const percentage = parseFloat(match.replace('%', ''));
    return roundPercentage(percentage);
  });
}

/**
 * Format AI response for display with proper Czech localization
 * @param text - AI response text
 * @param model - AI model used for generation
 * @returns Formatted response
 */
export function formatAIResponse(text: string, model?: string): string {
  const cleaned = cleanAIText(text);
  
  // Add model attribution if provided
  if (model) {
    return `${cleaned}\n\n— ${model}`;
  }
  
  return cleaned;
}
